/**
 * Larry's shipments on the phone: labels, links and the message to send a customer when their
 * order is here. The data and the rules are the backend's (lib/hq/parcels in the Medusa app).
 */
import { dateTime } from "./format"
import { MESSAGE_BY_KEY, render } from "./message-defaults"

const defaultTemplate = (key: string) => MESSAGE_BY_KEY.get(key)?.text ?? ""
import { localized } from "./i18n"
import type { DeliveryChoice, Shipment, ShipmentOrder } from "./medusa"

export const CHOICES: DeliveryChoice[] = ["box_now", "meetup", "home", "elta"]
export const CHOICE_LABEL: Record<DeliveryChoice, string> = localized({ box_now: "BOX NOW", meetup: "Meet-up", home: "Home delivery", elta: "ΕΛΤΑ" }, "delivery")
export const CHOICE_GREEK: Record<DeliveryChoice, string> = { box_now: "BOX NOW", meetup: "meet-up", home: "κατ’ οίκον παράδοση", elta: "ΕΛΤΑ" }

export const STATUS_LABEL: Record<Shipment["status"], string> = localized({ on_the_way: "On the way", in_greece: "Here", received: "Received", done: "Done" }, "shipment")
export const STATUS_COLOUR: Record<Shipment["status"], "blue" | "orange" | "green" | "grey"> = { on_the_way: "blue", in_greece: "orange", received: "green", done: "grey" }

export const photoUrl = (photo: { sha: string; ext: string }) => `/hq-media/larry/${photo.sha}.${photo.ext}`
/**
 * Where to follow a parcel. Larry's are Deutsche Post / DHL parcels (…DE): DHL's own page opens them
 * from a link. 17TRACK's site no longer reads a number from a link (it shows its demo parcel), so for
 * its app the number is copied instead.
 */
export const trackUrl = (tracking: string) =>
  /DE$/.test(tracking)
    ? `https://www.dhl.de/en/privatkunden/pakete-empfangen/verfolgen.html?piececode=${encodeURIComponent(tracking)}`
    : `https://t.17track.net/en#nums=${encodeURIComponent(tracking)}`
export const trackLabel = (tracking: string) => (/DE$/.test(tracking) ? "DHL" : "17TRACK")

/** "26 Sep, 14:05" · «26 Σεπ, 14:05», Athens time. */
export const shortWhen = (iso: string | null) => dateTime(iso)

/** The newest thing DHL said about any parcel of a shipment. */
export function latestOf(shipment: Shipment) {
  return shipment.parcels
    .map((p) => p.latest)
    .filter((l): l is NonNullable<typeof l> => !!l)
    .sort((a, b) => (b.at ?? "").localeCompare(a.at ?? ""))[0] ?? null
}

/** An Instagram handle that can be messaged; a name typed where a handle should be is not one. */
export const igHandle = (handle: string | null | undefined) => {
  const h = (handle ?? "").trim().replace(/^@/, "")
  return /^[A-Za-z0-9._]{1,30}$/.test(h) ? h : null
}

type Order = Extract<ShipmentOrder, { missing: false }>

/** The delivery the order goes with: the one chosen in HQ, else the sheet's. */
export function choiceOf(order: Order): DeliveryChoice | null {
  if (order.choice) return order.choice
  return (CHOICES as string[]).includes(order.delivery) ? (order.delivery as DeliveryChoice) : null
}

export type Progress = "to_contact" | "contacted" | "handed_over" | "completed" | "cancelled"

export function progressOf(order: Order): Progress {
  if (order.sheet_status === "cancelled") return "cancelled"
  if (order.handed_over_at) return "handed_over"
  if (order.sheet_status === "completed") return "completed"
  return order.contacted_at ? "contacted" : "to_contact"
}

export const PROGRESS_LABEL: Record<Progress, string> = localized(
  {
    to_contact: "To contact",
    contacted: "Contacted",
    handed_over: "Handed over",
    completed: "Completed in the sheet",
    cancelled: "Cancelled",
  },
  "progress"
)

const euro = (n: number) => `${Number.isInteger(n) ? n : n.toFixed(2).replace(".", ",")} €`

/** The DM when the order is here — in the formal «σας» the owners use for Instagram. Edited freely before sending. */
export function arrivalMessage(order: Order, template?: (key: string) => string): string {
  return arrivalText(order.number, choiceOf(order), order.left, template)
}

/**
 * The arrival message's placeholders (lib/message-defaults.ts «custom.arrived»): its delivery and
 * balance sentences are messages of their own, so `template` gives the wording in force for each.
 */
export function arrivalVars(number: string, choice: DeliveryChoice | null, left: number | null, template: (key: string) => string = defaultTemplate) {
  const delivery_line = choice ? render(template("custom.arrived.delivery_known"), { delivery: CHOICE_GREEK[choice] }) : template("custom.arrived.delivery_ask")
  const balance_line = left != null && left > 0 ? render(template("custom.arrived.balance"), { amount: euro(left) }) : ""
  return { number, delivery_line, balance_line }
}

/** The same message from its parts, in HQ's own wording unless `template` gives the owners'. */
export function arrivalText(number: string, choice: DeliveryChoice | null, left: number | null, template: (key: string) => string = defaultTemplate): string {
  return render(template("custom.arrived"), arrivalVars(number, choice, left, template))
}

/** Open first (to contact, then contacted), finished last — the order the owners work through them. */
export function sortForWork(orders: ShipmentOrder[]): ShipmentOrder[] {
  const rank = (o: ShipmentOrder) => (o.missing ? 5 : { to_contact: 0, contacted: 1, handed_over: 2, completed: 3, cancelled: 4 }[progressOf(o)])
  return [...orders].sort((a, b) => rank(a) - rank(b) || Number(a.number) - Number(b.number))
}

/**
 * The message to Larry about a problem found in a shipment, in the partner's words (his parcels
 * screen's `larryMessage`), naming the shop's bulk order when that is affected. Edited freely before
 * sending.
 */
export function larryProblemMessage(trackings: string[], problem: { orders: string[]; stock: boolean; note: string | null }): string {
  const nums = problem.orders.map((o) => `#${o}`).join(", ")
  const what = nums && problem.stock ? `these order numbers: ${nums}, and with our bulk order` : nums ? `these order numbers: ${nums}` : problem.stock ? "our bulk order" : ""
  let message = `Hi Larry! About parcel${trackings.length > 1 ? "s" : ""} ${trackings.join(", ")}: I received ${trackings.length > 1 ? "them" : "it"}, but there is a problem${what ? ` with ${what}` : ""}.`
  const note = (problem.note ?? "").trim().replace(/[.!?]+$/, "")
  if (note) message += ` Details: ${note}.`
  return `${message} Could you please check? Thank you!`
}
